import { clsx } from 'clsx'
import { X } from 'lucide-react'
import {
  useCancelSchedule,
  useGameweekSchedules,
  type ScheduleAction,
  type ScheduledJob,
} from '../queries'
import { ScheduleForm } from './ScheduleForm'
import styles from './GameweekScheduleCard.module.css'

type GameweekScheduleCardProps = {
  gameweekId:     string
  gameweekNumber: number
}

const ACTION_LABEL: Record<ScheduleAction, string> = {
  LOCK:     'Lock',
  FETCH:    'Fetch stats',
  PROCESS:  'Process',
  FINALIZE: 'Finalize',
  REOPEN:   'Reabrir janela',
  RESULTS:  'Buscar resultados',   // Sobrevivente's sweep; offered in its own panel
}

export function GameweekScheduleCard({ gameweekId, gameweekNumber }: GameweekScheduleCardProps) {
  const { data: schedules, isPending, isError } = useGameweekSchedules(gameweekId)
  const cancel = useCancelSchedule(gameweekId)

  return (
    <div className={styles.card}>
      <h2 className={styles.title}>Agendamentos — GW{gameweekNumber}</h2>
      <p className={styles.hint}>
        Dispara uma ação da rodada (Lock, Fetch, Process, Finalize) por Hangfire no horário
        escolhido. Substituto manual enquanto os gatilhos automáticos não existem.
      </p>

      {isPending && <div className={styles.empty}>Carregando agendamentos…</div>}
      {isError && <div className={styles.empty}>Erro ao carregar agendamentos.</div>}
      {!isPending && !isError && schedules && schedules.length === 0 && (
        <div className={styles.empty}>Nenhum agendamento. Crie um abaixo.</div>
      )}

      {schedules && schedules.length > 0 && (
        <div className={styles.list}>
          {schedules.map((s) => (
            <ScheduleRow
              key={s.id}
              schedule={s}
              onCancel={() => cancel.mutate(s.id)}
              isCancelling={cancel.isPending && cancel.variables === s.id}
            />
          ))}
        </div>
      )}

      <ScheduleForm gameweekId={gameweekId} />
    </div>
  )
}

function ScheduleRow({
  schedule, onCancel, isCancelling,
}: {
  schedule:     ScheduledJob
  onCancel:     () => void
  isCancelling: boolean
}) {
  const canCancel = schedule.status === 'SCHEDULED'
  return (
    <div className={styles.item}>
      <div className={styles.itemMain}>
        <div className={styles.itemTop}>
          <span className={styles.actionBadge}>{ACTION_LABEL[schedule.action]}</span>
          <span className={styles.cadence}>{formatCadence(schedule)}</span>
        </div>
        <span className={styles.meta}>
          {schedule.runCount} execuç{schedule.runCount === 1 ? 'ão' : 'ões'}
          {schedule.lastRunAt ? ` · última ${formatDateTime(schedule.lastRunAt)}` : ''}
        </span>
        {schedule.lastError && <p className={styles.error}>Erro: {schedule.lastError}</p>}
      </div>

      <div className={styles.itemSide}>
        <span className={clsx(styles.statusPill, styles[`status_${schedule.status}`])}>
          {schedule.status}
        </span>
        {canCancel && (
          <button type="button" className={styles.cancelBtn} onClick={onCancel} disabled={isCancelling}>
            <X size={11} /> {isCancelling ? 'Cancelando…' : 'Cancelar'}
          </button>
        )}
      </div>
    </div>
  )
}

function formatCadence(s: ScheduledJob): string {
  switch (s.mode) {
    case 'ONCE':
      return s.runAt ? `Uma vez · ${formatDateTime(s.runAt)}` : 'Uma vez'
    case 'INTERVAL':
      return `A cada ${formatInterval(s.intervalMinutes)}`
    case 'WINDOW':
      return `A cada ${formatInterval(s.intervalMinutes)} · janela ${formatDateTime(s.windowStart)}–${formatDateTime(s.windowEnd)}`
    case 'CRON':
      return `cron: ${s.cron ?? ''}`
  }
}

function formatInterval(minutes: number | null): string {
  if (minutes === null) return '—'
  if (minutes % 60 === 0) {
    const h = minutes / 60
    return `${h} h`
  }
  return `${minutes} min`
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}
